import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  getModulesConfigAuditListQueryKey,
  getModulesConfigClassRetrieveQueryKey,
  getModulesConfigClassStudentsListQueryKey,
  useModulesCatalogList,
  useModulesConfigClassStudentsList,
  useModulesConfigStudentSet,
  useModulesConfigStudentUnset,
} from '@/api/client'
import type { Me, ModuleConfigItem, StudentRosterRow } from '@/api/model'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SOURCE_LABELS } from '@/lib/module-config'
import { studentLogin } from '@/lib/student-login'

/**
 * TR-1200 R13 — per-student module overrides, opened from a class roster row. The
 * roster row is a `StudentRosterRow` (keyed by membership `id`); the module write
 * endpoint keys on the student *account* `student_id`. The only field shared by both
 * shapes is `journal_number`, so we match the roster row to the module `StudentModulesRow`
 * by journal number, then write with that row's `student_id`.
 *
 * Superadmin sets/removes a single student's override; admin sees it read-only;
 * teachers never reach here (the row action is hidden for them).
 */
export function StudentModulesDialog({
  classId,
  row,
  role,
  onOpenChange,
}: {
  classId: number
  row: StudentRosterRow | null
  role: Me['role'] | undefined
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const isOperator = role === 'superadmin' || role === 'admin'
  const open = row !== null
  const canEdit = role === 'superadmin'

  const catalog = useModulesCatalogList()
  const students = useModulesConfigClassStudentsList(classId, {
    query: { enabled: open && isOperator },
  })

  const setOverride = useModulesConfigStudentSet()
  const unsetOverride = useModulesConfigStudentUnset()
  const writePending = setOverride.isPending || unsetOverride.isPending

  const catalogItems = catalog.data?.status === 200 ? catalog.data.data : []
  const toggleable = catalogItems.filter((entry) => entry.toggleable)
  const studentRows = students.data?.status === 200 ? students.data.data : []
  // Match by journal number — see the file header for the id-bridge rationale.
  const studentRow = row
    ? studentRows.find((s) => s.journal_number === row.journal_number)
    : undefined
  const itemsByModule = new Map<string, ModuleConfigItem>(
    (studentRow?.modules ?? []).map((item) => [item.module, item]),
  )

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: getModulesConfigClassStudentsListQueryKey(classId),
    })
    // The class-level R13 signal is derived from the existence of student overrides.
    await queryClient.invalidateQueries({
      queryKey: getModulesConfigClassRetrieveQueryKey(classId),
    })
    await queryClient.invalidateQueries({ queryKey: getModulesConfigAuditListQueryKey() })
  }

  const write = async (action: () => Promise<{ status: number }>) => {
    setError(null)
    const response = await action()
    if (response.status !== 200) {
      setError('Nie udało się zapisać zmiany. Odśwież widok i spróbuj ponownie.')
      return
    }
    await invalidate()
  }

  const setStudent = (module: string, enabled: boolean) => {
    if (!studentRow) return
    void write(() =>
      setOverride.mutateAsync({ studentId: studentRow.student_id, module, data: { enabled } }),
    )
  }

  const removeStudent = (module: string) => {
    if (!studentRow) return
    void write(() => unsetOverride.mutateAsync({ studentId: studentRow.student_id, module }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Moduły ucznia</DialogTitle>
          <DialogDescription>
            {row ? `Login ${studentLogin(row)}` : ''} — nadpisania indywidualne modułów
            opcjonalnych. Moduły zawsze aktywne nie podlegają wyłączeniu dla ucznia.
          </DialogDescription>
        </DialogHeader>

        {students.isLoading ? (
          <p className="text-sm text-muted-foreground">Ładowanie…</p>
        ) : !studentRow ? (
          <p className="text-sm text-muted-foreground">
            Brak danych o modułach tego ucznia. Odśwież widok.
          </p>
        ) : (
          <div className="grid gap-3">
            {toggleable.map((entry) => {
              const item = itemsByModule.get(entry.module)
              if (!item) return null
              const overridden = item.override != null
              return (
                <div
                  key={entry.module}
                  className="flex items-center justify-between gap-4 rounded-lg border px-4 py-3"
                >
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{entry.label}</span>
                      <StatusBadge tone={item.effective_enabled ? 'green' : 'zinc'}>
                        {item.effective_enabled ? 'Włączony' : 'Wyłączony'}
                      </StatusBadge>
                      <StatusBadge tone="outline">{SOURCE_LABELS[item.source]}</StatusBadge>
                      {item.diverges_from_class && (
                        <StatusBadge tone="amber">Inaczej niż klasa</StatusBadge>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex shrink-0 gap-2">
                      {overridden && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={writePending}
                          onClick={() => removeStudent(entry.module)}
                        >
                          Usuń nadpisanie
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant={item.effective_enabled ? 'outline' : 'default'}
                        disabled={writePending}
                        onClick={() => setStudent(entry.module, !item.effective_enabled)}
                      >
                        {item.effective_enabled ? 'Wyłącz' : 'Włącz'}
                      </Button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </DialogContent>
    </Dialog>
  )
}
